// 出荷日/搬入日など「完了日でグルーピングしたAccordion一覧」の共通実装。
// CarryOutPage(出荷日タブ)・CarryInPage(搬入日タブ)で同型のグルーピング表示が重複していたため抽出(C-2)。

import { ReactNode, useMemo } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Stack, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export type DateGroupSummary = {
  completedCount: number;
  totalCount: number;
  plannedTotal: number;
  attachedTotal: number;
};

export function DateGroupedAccordions<T>({
  rows,
  groupKeyOf,
  summaryOf,
  dateChipLabel,
  countUnitLabel,
  attachedUnitLabel,
  renderRow,
  emptyState,
}: {
  rows: T[];
  groupKeyOf: (row: T) => string;
  summaryOf: (rows: T[]) => DateGroupSummary;
  dateChipLabel: string;
  countUnitLabel: string;
  attachedUnitLabel: string;
  renderRow: (row: T) => ReactNode;
  emptyState: ReactNode;
}) {
  const groups = useMemo(() => {
    const map = new Map<string, T[]>();
    rows.forEach((row) => {
      const key = groupKeyOf(row);
      const list = map.get(key) ?? [];
      list.push(row);
      map.set(key, list);
    });
    return Array.from(map.entries());
  }, [rows, groupKeyOf]);

  if (groups.length === 0) {
    return <>{emptyState}</>;
  }

  return (
    <Box>
      {groups.map(([key, list]) => {
        const summary = summaryOf(list);
        const ratio = summary.plannedTotal > 0 ? Math.floor((summary.attachedTotal / summary.plannedTotal) * 100) : 0;
        return (
          <Accordion key={key} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" gap={1.5} flex={1} mr={2} flexWrap="wrap">
                <Chip size="small" variant="outlined" label={dateChipLabel} />
                <Typography variant="body2" fontWeight={600}>{key}</Typography>
                <Chip size="small" color="info" variant="outlined" label={`${list.length}${countUnitLabel}`} />
                <Chip size="small" color={summary.completedCount === summary.totalCount ? "success" : "warning"} variant="outlined" label={`${dateChipLabel}完了 ${summary.completedCount}/${summary.totalCount}`} />
                <Chip size="small" variant="outlined" label={`${attachedUnitLabel} ${summary.attachedTotal}/${summary.plannedTotal} (${ratio}%)`} />
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ backgroundColor: "grey.50", p: 1 }}>
              <Stack gap={1}>
                {list.map((row) => renderRow(row))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
